import React from 'react';
import {
    Avatar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    List,
    ListItem,
    ListItemText,
    Typography,
} from '@material-ui/core';

import nameInitials from '../../../name-initials';
import useStyles from './style';

const formatDateToDDMMYY = (dateStr) => {
    if (!dateStr) {
        return '';
    }

    try {
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
            const parts = dateStr.split('T')[0].split('-');
            if (parts.length === 3) {
                return `${parts[2]}/${parts[1]}/${parts[0].slice(-2)}`;
            }
        }

        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) {
            return String(dateStr);
        }

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
    } catch {
        return String(dateStr);
    }
};

const formatTimestamp = (dateStr) => {
    if (!dateStr) {
        return '';
    }

    try {
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) {
            return String(dateStr);
        }

        const formatted = date.toLocaleString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/Los_Angeles',
        });

        return formatted.replace(/\//g, '-');
    } catch {
        return String(dateStr);
    }
};

const parseEntryValue = (entry, isDate) => {
    if (!entry || typeof entry !== 'object') {
        return null;
    }

    if (entry.fromValue && entry.toValue) {
        const oldDate = formatDateToDDMMYY(entry.fromValue);
        const newDate = formatDateToDDMMYY(entry.toValue);
        return {
            text: `Updated Target Completion Date From ${oldDate} to ${newDate}`,
            oldDate,
            newDate,
        };
    }

    if (entry.value) {
        return {
            text: isDate ? formatDateToDDMMYY(entry.value) : entry.value,
            oldDate: null,
            newDate: null,
        };
    }

    return null;
};

const FieldHistoryDialog = ({ open, label, history = [], isDate = false, onClose }) => {
    const classes = useStyles();
    const items = Array.isArray(history) ? history : [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            disableBackdropClick
            disableEscapeKeyDown
            PaperProps={{
                className: classes.dialogPaper,
            }}
        >
            <DialogTitle className={classes.dialogTitle} disableTypography>
                <Typography variant="h6" component="span">
                    {`${label ?? ''} History`}
                </Typography>
            </DialogTitle>
            <Divider />
            <DialogContent className={classes.dialogContent}>
                {items.length === 0 ? (
                    <Box display="flex" alignItems="center" justifyContent="center" py={3}>
                        <Typography color="textSecondary">No history available.</Typography>
                    </Box>
                ) : (
                    <List className={classes.listRoot} dense>
                        {items.map((entry, index) => {
                            const isTracked = typeof entry === 'object' && entry != null && 'value' in entry;
                            if (!isTracked) {
                                return null;
                            }

                            const timeStr = entry.updatedAtPST
                                || (entry.updatedAt ? formatTimestamp(entry.updatedAt) : '');
                            const byStr = entry.updatedBy ? entry.updatedBy : '';
                            const parsedEntry = parseEntryValue(entry, isDate);

                            let primaryText = parsedEntry?.text || entry.value || '';
                            if (parsedEntry?.oldDate && parsedEntry?.newDate) {
                                primaryText = (
                                    <>
                                        Updated Target Completion Date
                                        {' '}
                                        <strong>
                                            From
                                            {' '}
                                            {parsedEntry.oldDate}
                                            {' '}
                                            to
                                            {' '}
                                            {parsedEntry.newDate}
                                        </strong>
                                    </>
                                );
                            }

                            return (
                                <React.Fragment key={index}>
                                    {index > 0 && <Divider component="li" />}
                                    <ListItem className={classes.listItem}>
                                        <Typography className={classes.timestamp}>
                                            {timeStr}
                                        </Typography>
                                        <Box className={classes.avatarContainer}>
                                            <Avatar className={classes.avatar}>
                                                {byStr ? nameInitials(byStr) : 'U'}
                                            </Avatar>
                                        </Box>
                                        <ListItemText
                                            className={classes.listItemText}
                                            primary={primaryText}
                                            secondary={byStr ? `By ${byStr}` : ''}
                                            primaryTypographyProps={{ variant: 'body1' }}
                                            secondaryTypographyProps={{ variant: 'caption', color: 'textSecondary' }}
                                        />
                                    </ListItem>
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            <Divider />
            <DialogActions className={classes.dialogActions}>
                <Button
                    variant="contained"
                    onClick={onClose}
                    className={classes.closeButton}
                >
                    CLOSE
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FieldHistoryDialog;
